import { FetchedList } from "../redux-util.types";

interface Status {
  status_type: boolean;
  status_message: string;
  status_code: number;
}

export interface SurveyCloneResponse {
  extSurveyId: string;
}

export interface AiServiceTriggerResponseJSON {
  ext_survey_id: string;
  service: string;
  status: string;
}

export interface ModerationItem {
  id: string;
  field: string;
  status: string;
  concerns: string[];
  message: string;
  text: string;
}

export interface ModerationResponse {
  status: Status;
  item: {
    [key: string]: ModerationItem | string;
    survey_status: string;
  };
}

export interface ModerationIssue {
  readonly issueKey: string;
  readonly offendingText: string;
  readonly concernString: string;
}

export interface ModerationCheckResults {
  readonly count: number;
  readonly didPass: boolean;
  readonly issues: ModerationIssue[];
}

export interface Brand {
  id: string;
  name: string;
  config: {
    bg_color: string;
    font_color: string;
    button_color: string;
    font_secondary_color: string;
    answer_border_color: string;
    rtr_size: string;
    footer_link: string;
    default_toast: Partial<SurveyToast>;
  }
  image_1: string;
  image_2: string;
  toast_image: string;
}

export interface SurveyToast {
  id: string;
  name: string;
  title: string;
  subtitle: string | null;
  button_copy: string | null;
  button_url: string | null;
  image_url: string | null;
  pixel: string | null;
  custom_tag: string | null;
  survey: string;
}

export enum SurveyStatus {
  Building = 'building',
  Draft = 'draft',
  Complete = 'complete',
  Live = 'live',
  Review = 'review',
  Rejected = 'rejected',
  Retry = 'retry',
  Failed = 'failed'
}
export function toSurveyStatus(status: string | null): SurveyStatus {
  switch (status) {
    case 'draft':
      return SurveyStatus.Draft;
    case 'complete':
      return SurveyStatus.Complete;
    case 'live':
      return SurveyStatus.Live;
    case 'review':
      return SurveyStatus.Review;
    case 'rejected':
      return SurveyStatus.Rejected;
    case 'building':
      return SurveyStatus.Building;
    case 'retry':
      return SurveyStatus.Retry;
    case 'failed':
      return SurveyStatus.Failed;
    default:
      return SurveyStatus.Draft;
  }
}

export enum SurveyDisplayStatus {
  Live = 'Live',
  Draft = 'Draft',
  Complete = 'Complete'
}

export enum SurveyQuestionType {
  SingleSelect = 'single_select',
  MultiSelect = 'multi_select',
  Freeform = 'free_form'
}
export enum SaveStatusEnum {
  Syncing = 'Syncing',
  JustSynced = 'Just synced',
  Synced = 'Synced'
}
export const defaultSaveStatus: SaveStatus = {
  status: SaveStatusEnum.Synced,
};
interface SaveStatus {
  status: SaveStatusEnum;
}
interface UploadImage {
  item: {
    obj_uuid: string;
    type: string;
    url: string;
  };
  status: Status;
}

// maybe change the name of this to SurveyListObjectJSON
interface SurveyJSON {
  readonly id: string;
  readonly image_url?: string;
  readonly created_at: string;
  readonly created_by: string;
  readonly end_date?: string;
  readonly ext_survey_id: string;
  readonly title: string;
  readonly completed_users: number;
  readonly ai_completed_users: number;
  readonly max_completes?: number;
  readonly ai_max_completes?: number;
  readonly start_date?: string;
  readonly survey_status: SurveyStatus;
  readonly question_count: number;
  readonly brand: Brand | null;
  readonly started: {
    readonly human: number;
    readonly ai: number;
  };
  readonly completed: {
    readonly human: number;
    readonly ai: number;
  };
  readonly survey_type: 'ai' | 'rtr' | null;
  readonly panel_id: string | null;
}

// maybe change the name of this to SurveyListObject or SurveyListItem?
interface Survey {
  id: string;
  imageUrl?: string
  createdAt: string;
  createdBy: string;
  endDate?: string;
  extSurveyId: string;
  title: string;
  completedUsers: number;
  aiCompletedUsers: number;
  maxCompletes?: number;
  maxAICompletes?: number;
  startDate?: string;
  surveyStatus: SurveyStatus;
  questionCount: number;
  brand: Brand | null;
  survey_pixel?: string | null;
  started: {
    human: number;
    ai: number;
  };
  completed: {
    human: number;
    ai: number;
  };
  surveyType: 'ai' | 'rtr' | null;
  panel: string | null;
}

export const defaultSurvey: Survey = {
  id: '',
  createdAt: '',
  createdBy: '',
  extSurveyId: '',
  title: '',
  completedUsers: 0,
  aiCompletedUsers: 0,
  maxCompletes: undefined,
  startDate: undefined,
  surveyStatus: SurveyStatus.Draft,
  questionCount: 0,
  brand: null,
  survey_pixel: null,
  started: {
    human: 0,
    ai: 0,
  },
  completed: {
    human: 0,
    ai: 0,
  },
  surveyType: null,
  panel: null,
};

interface BaseStatsJSON {
  readonly ai_users: number;
  readonly human_users: number;
  readonly rtr_users: number;
  readonly image_url?: string | null;
  readonly title: string;
  readonly display_name?: string;
  readonly total_users: number;
}

interface BaseStats {
  readonly aiPercentage: number;
  aiUsers: number;
  humanUsers: number;
  readonly humanPercentage: number;
  readonly imageUrl?: string;
  rtrUsers: number;
  readonly rtrPercentage: number;
  readonly title: string;
  readonly displayName: string;
  totalUsers: number;
  readonly totalPercentage: number
}

export enum DemographicsKey {
  education = 'education',
  employment = 'employment',
  householdIncome = 'household_income',
  maritalStatus = 'marital_status',
  children = 'children',
  pets = 'pets',
  ethnicity = 'ethnicity',
  placeOfOrigin = 'place_of_origin',
  politicalAffiliation = 'political_affiliation',
  registeredToVote = 'registered_to_vote',
  language = 'language',
  gender = 'gender',
  ageGroup = 'age_group',
  dma = 'dma'
}

type DemographicsStatsJSON = {
  [key in DemographicsKey]: { [id: string]: BaseStatsJSON }
};

type FullDemographicsStats = {
  [key in DemographicsKey]: BaseStats[]
};

type DemographicsStats = Partial<FullDemographicsStats>;

interface AllFeedbackJSON {
  readonly ai_answers: FeedbackJSON[];
  readonly human_answers: FeedbackJSON[];
  readonly rtr_answers: FeedbackJSON[];
}

interface FeedbackJSON {
  created_at: string;
  feedback: string;
}

interface AllFeedback {
  readonly aiAnswers: Feedback[];
  readonly humanAnswers: Feedback[];
  readonly rtrAnswers: Feedback[];
}

interface Feedback {
  createdAt: string;
  feedback: string;
  userType: FeedbackUserType;
}

type FeedbackUserType = 'ai' | 'human' | 'rtr';

interface FreeformJSON {
  readonly ai_users: string[];
  readonly human_users: string[];
  readonly rtr_users: string[];
}

interface FreeformAnswer {
  readonly createdAt?: string;
  readonly answer: string;
  readonly userType: FeedbackUserType;
}

interface AllFreeformAnswers {
  readonly aiUsers: FreeformAnswer[];
  readonly humanUsers: FreeformAnswer[];
  readonly rtrUsers: FreeformAnswer[];
}

interface SurveyAnswerStatsJSON extends BaseStatsJSON {
  readonly id: string;
  readonly demographics?: DemographicsStatsJSON;
  readonly feedback?: AllFeedbackJSON;
}

interface AnswerStats extends BaseStats {
  readonly id: string;
  readonly demographics?: DemographicsStats;
  feedbacks?: AllFeedback;
}

interface SurveyQuestionStatsJSON extends BaseStatsJSON {
  readonly id: string;
  readonly answers: { [id: string]: SurveyAnswerStatsJSON }
  readonly ['free_response_answers']?: FreeformJSON;
  readonly unique_ai_users: number;
  readonly unique_human_users: number;
}

interface QuestionStats extends BaseStats {
  readonly id: string;
  answers: AnswerStats[];
  freeformAnswers?: AllFreeformAnswers;
  readonly demographics?: DemographicsStats;
  readonly unique_ai_users: number;
  readonly unique_human_users: number;
}

/** New API shape: top-level total_users is { total, started, completed }; no ai/human split */
export interface TotalUsersStats {
  readonly total: number;
  readonly started: number;
  readonly completed: number;
}

/** New stats response: ext_survey_id, title, total_users (object or number), questions (shape unchanged) */
interface SurveyStatsJSON {
  readonly ext_survey_id: string;
  readonly title?: string;
  /** New shape: unified total_users object; legacy: number */
  readonly total_users?: TotalUsersStats | number;
  readonly questions: { [id: string]: SurveyQuestionStatsJSON };
  /** Legacy top-level fields when API returns old shape */
  readonly ai_users?: number;
  readonly human_users?: number;
  readonly rtr_users?: number;
  readonly image_url?: string | null;
  readonly display_name?: string;
}

interface SurveyStats extends BaseStats {
  readonly extSurveyId: string;
  /** New shape: unified total_users for sidebar (total, started, completed) */
  readonly total_users?: TotalUsersStats;
  readonly questions: QuestionStats[];
}

// maybe change the name to SurveyJSON?
interface SurveyFullJSON {
  readonly title: string;
  readonly ext_survey_id: string;
  readonly points: number;
  readonly questions: SurveyQuestionJSON[];
  readonly audience: string | null;
  readonly category: string | null;
  readonly image_url: string | null;
  readonly rank: number | null;
  readonly ranked_at: string | null;
  readonly start_date: string | null;
  readonly end_date: string | null;
  // question_count:  number;
  readonly is_published: boolean;
  readonly max_completes: number | null;
  readonly ai_max_completes: number | null;
  readonly survey_status: string | null;
  readonly brand: Brand | null;
  readonly survey_pixel: string | null;
  readonly survey_type: 'ai' | 'rtr' | null;
  readonly panel_id: string | null;
}

// Do not change these, they're server values
type ShuffleMode = 'no_shuffle' | 'random' | 'reverse';

interface SurveyQuestionJSON {
  readonly id: string;
  readonly title: string;
  readonly type: string;
  readonly points: number;
  readonly answers: SurveyAnswerJSON[];
  readonly response_ids: string[];
  readonly shuffle: ShuffleMode;
  readonly next_question: string | null;
  readonly image_url: string | null;
  readonly total_respondents: number;
  readonly question_order: number;
  readonly has_feedback: boolean;
}

interface SurveyAnswerJSON {
  readonly id: string;
  readonly title: string;
  readonly answer_pixel: string | null;
  readonly answer_branch: string | null;
  readonly is_final_question: boolean;
  readonly image_url: string | null;
  readonly user_percent: number;
  readonly total_count: number;
  readonly toast: string | SurveyToast | null;
}

interface SurveyResponderJSON {
  readonly ext_survey_id: string;
  readonly max_completes: string;
  readonly panel_id: string;
}

interface SurveyResponder {
  readonly extSurveyId: string;
  readonly maxCompletes: string;
}

interface SummaryJSON {
  readonly alignment_score: number | null;
  readonly summaries: string[];
}

interface SurveySummaryDataJSON {
  readonly survey: SummaryJSON;
  readonly questions: { [id: string]: SummaryJSON }
}

interface SurveySummaryQuestion {
  readonly id: string;
  readonly alignmentScore?: number;
  readonly summaries: string[];
}

interface SurveySummaryData {
  summaries: string[];
  readonly alignmentScore?: number;
  readonly questions: SurveySummaryQuestion[];
}

interface Answer {
  id: string;
  title: string;
  answerPixel: string | null;
  answerBranch: string | null;
  isFinalQuestion: boolean;
  imageUrl: string | null;
  userPercent: number;
  totalCount: number;
  toast: string | null;
}

export const defaultAnswer: Answer = {
  id: '',
  title: '',
  answerPixel: null,
  answerBranch: null,
  isFinalQuestion: false,
  imageUrl: null,
  userPercent: 0,
  totalCount: 0,
  toast: null,
};


interface Question {
  id: string;
  title: string;
  type: string;
  points: number;
  answers: Answer[];
  responseIds: string[];
  shuffle: ShuffleMode;
  nextQuestion: string | null;
  questionOrder: number;
  imageUrl: string | null;
  totalRespondents: number;
  hasFeedback: boolean;
}

interface QuestionCreationData {
  title: string,
  type?: SurveyQuestionType,
  question_order: number,
  shuffle: boolean,
  survey: string,
  ext_survey_id: string
}

interface QuestionUpdateData {
  ext_survey_id: string,
  title: string,
  question_order: number,
  survey: string,
  question_id: string,
  image_url?: string,
  type?: SurveyQuestionType,
  shuffle?: ShuffleMode,
  has_feedback?: boolean,
}

export const defaultQuestion: Question = {
  id: '',
  title: '',
  type: '',
  points: 0,
  answers: [],
  responseIds: [],
  shuffle: 'random',
  nextQuestion: null,
  questionOrder: 0,
  imageUrl: null,
  totalRespondents: 0,
  hasFeedback: false,
};

interface SurveyFull {
  title: string;
  extSurveyId: string;
  points: number;
  questions: Question[];
  audience: string | null;
  category: string | null;
  imageUrl: string | null;
  rank: number | null;
  rankedAt: string | null;
  startDate: string | null;
  endDate: string | null;
  isPublished: boolean;
  maxAICompletes: number | null;
  maxCompletes: number | null;
  extMediaPlanId?: string;
  surveyStatus: SurveyStatus;
  brand: Brand | null;
  surveyPixel: string | null;
  isPulsePolling?: boolean;
  surveyType: 'ai' | 'rtr' | null;
  panelId: string | null;
}

export const defaultSurveyFull: SurveyFull = {
  title: '',
  extSurveyId: '',
  points: 0,
  questions: [],
  audience: null,
  category: null,
  imageUrl: null,
  rank: null,
  rankedAt: null,
  startDate: null,
  endDate: null,
  isPublished: false,
  maxCompletes: 15,
  maxAICompletes: null,
  surveyStatus: SurveyStatus.Draft,
  brand: null,
  surveyPixel: null,
  surveyType: null,
  panelId: null,
};

interface SurveyAudienceCriteria {
  readonly 'State'?: string[];
  readonly 'Education'?: string[];
  readonly 'Employment'?: string[];
  readonly 'Household income'?: string[];
  readonly 'Marital status'?: string[];
  readonly 'Children'?: string[];
  readonly 'Pets'?: string[];
  readonly 'Ethnicity'?: string[];
  readonly 'Place of origin'?: string[];
  readonly 'Political affiliation'?: string[];
  readonly 'Registered to vote'?: string[];
  readonly 'Gender'?: string[];
  readonly 'Age Group'?: string[];
  readonly 'DMA'?: string[];
  readonly 'city'?: string[];
  readonly 'region'?: string[];
  readonly 'user_type'?: string[];
}
type SurveyAudienceCriteriaKey = keyof SurveyAudienceCriteria;

export const defaultSurveyAudienceCriteria: SurveyAudienceCriteria = {
};


interface SurveyAudienceJSON {
  readonly criteria: SurveyAudienceCriteria;
  readonly total_users: number;
  readonly id: string;
  readonly created_at: string;
  readonly updated_at: string;
  readonly title: string;
  readonly surveys: string | null;
  readonly status: number;
  readonly created_by: string;
}


interface SurveyAudience {
  criteria: SurveyAudienceCriteria;
  totalUsers: number;
  id: string;
  createdAt: string;
  updatedAt: string;
  title: string;
  surveys: string | null;
  status: number;
  createdBy: string;
}

interface SurveyAudienceSmallJSON {
  readonly criteria: SurveyAudienceCriteria;
  readonly total_users: number;
  readonly id: string;
}

interface SurveyAudienceSmall {
  criteria: SurveyAudienceCriteria;
  totalUsers: number;
  id: string;
}

type SurveyData = FetchedList<Survey>;

interface ISurveyState {
  readonly list: SurveyData
}

export enum AnswerSource {
  All, AI, Human, RTR
}

export interface PulsePollingConfigResponse {
  id: string;
  survey: string;
  created_at: string;
  updated_at: string;
  status: string;
  run_config_status: string;
  interval: 'day' | 'week' | 'month';
  completes: number;
  start_date: string;
  end_date: string;
  created_by: string;
}

interface PulsePollingSurveyRunConfigJSON {
  readonly id: string;
  readonly interval: string;
  readonly start_date: string;
}

interface PulsePollingSurveyRunJSON {
  readonly id: string;
  readonly iteration: number;
  readonly started_at: string;
}

interface PulsePollingAnswerJSON {
  readonly id: string;
  readonly title: string;
  readonly image_url: string | null;
  readonly human_users: number;
  readonly ai_users: number;
  readonly total_users: number;
}

interface PulsePollingQuestionRunJSON {
  readonly survey_run_id: string;
  readonly survey_run_iteration: number;
  readonly survey_run_started_at: string;
  readonly answers: { [id: string]: PulsePollingAnswerJSON };
}

interface PulsePollingQuestionJSON {
  readonly id: string;
  readonly title: string;
  readonly image_url: string | null;
  readonly type: string;
  readonly answer_keys: string[];
  readonly survey_runs: PulsePollingQuestionRunJSON[];
}

export interface PulsePollingSurveyDataJSON {
  readonly ext_survey_id: string;
  readonly survey_run_config_id: PulsePollingSurveyRunConfigJSON;
  readonly survey_runs: PulsePollingSurveyRunJSON[];
  readonly questions: { [id: string]: PulsePollingQuestionJSON };
}

/** One attribute from the type endpoint (e.g. city, region, user_type) with display and values for selectors */
export interface SurveyTypeAttributeDef {
  key: string;
  name: string;
  display: string;
  values: string[];
}

/** Nested attributes from GET b2b/survey/<id>/type */
export interface SurveyTypeAttributesInner {
  subject_count: number;
  attribute_coverage_threshold: number;
  attributes: SurveyTypeAttributeDef[];
}

/** Response from GET b2b/survey/<id>/type – determines which filters to show on results */
export interface SurveyTypeResponse {
  ext_survey_id: string;
  type: 'ai' | 'rtr';
  total: number;
  /** Date range in days; trends are shown only when date_range > 7 */
  date_range?: number;
  attributes: SurveyTypeAttributesInner;
}

/** Location/demographic fields per subject; structure can vary by user type. All values from API are strings. */
export interface SubjectDemographics {
  city?: string;
  region?: string;
  region_code?: string;
  country?: string;
  postal_code?: string;
  latitude?: string;
  longitude?: string;
  accuracy_radius_km?: string;
  location_confidence?: string;
  user_type?: string;
  average_income?: string;
  population_density?: string;
  is_vpn?: string;
  [key: string]: string | undefined;
}

/** One response record from GET b2b/survey/<id>/demographics */
export interface SurveyDemographicRecord {
  created_at?: string;
  type?: string;
  answer_text?: string;
  feedback?: string;
  survey_question?: { id?: string; title?: string; type?: string };
  survey_question_answer?: { id?: string; title?: string };
  subject?: { id?: string; external_id?: string };
  subject_demographics?: SubjectDemographics;
}

/** Response from GET b2b/survey/<id>/demographics */
export interface SurveyDemographicsResponse {
  answers: SurveyDemographicRecord[];
  count: number;
  ext_survey_id: string;
}

export type {
  UploadImage,
  SaveStatus,
  SurveyJSON,
  Survey,
  ISurveyState,
  SurveyData,
  SurveyFullJSON,
  SurveyFull,
  SurveyAnswerJSON,
  SurveyQuestionJSON,
  Question,
  Answer,
  SurveyAudienceJSON,
  SurveyAudience,
  SurveyAudienceCriteria,
  SurveyAudienceSmallJSON,
  SurveyAudienceSmall,
  QuestionCreationData,
  QuestionUpdateData,
  BaseStatsJSON,
  BaseStats,
  SurveyAnswerStatsJSON,
  SurveyQuestionStatsJSON,
  SurveyStatsJSON,
  AnswerStats,
  QuestionStats,
  SurveyStats,
  DemographicsStatsJSON,
  FullDemographicsStats,
  DemographicsStats,
  SurveyAudienceCriteriaKey,
  AllFeedback,
  AllFeedbackJSON,
  Feedback,
  FeedbackJSON,
  FeedbackUserType,
  ShuffleMode,
  SummaryJSON,
  SurveySummaryDataJSON,
  SurveySummaryQuestion,
  SurveySummaryData,
  FreeformJSON,
  FreeformAnswer,
  AllFreeformAnswers,
  SurveyResponder,
  SurveyResponderJSON,
};

